import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import {
  FilterCondition,
  FilterDTO,
  FilterOperator,
  PagedResultDTOOfParkerDTO,
  PagedResultDTOOfParkerTurnoverReportDTO,
  PagedResultDTOOfZoneDTO,
  ParkerClient,
  ParkerTurnoverReportClient,
  ParkerTurnoverReportParameterDTO,
  ParkerTurnoverReportSummaryDTO,
  SavedReportFileDTO,
  SortDirection,
  ZoneClient,
} from '../../../../base/models/models.swagger.generated';
import { catchError, tap } from 'rxjs/operators';
import { ErrorType } from '../../../../core/routing/error-types';
import { ClientService } from '../../../../base/services/client/client.service';
import { ToastService } from '../../../../base/services/toast.service';
import { SaveOptions } from '../../reports-footer/save-options';
import { ShowInactiveService } from '../../../../base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from '../../../../base/shared/syncfusion/adaptor-service.interface';

@Injectable()
export class ParkerTurnoverReportService extends ShowInactiveService implements IAdaptorService {

  public totalAmount = 0;
  public searchParameters: ParkerTurnoverReportParameterDTO = new ParkerTurnoverReportParameterDTO();

  constructor(private parkerClient: ParkerClient,
              private zoneClient: ZoneClient,
              private clientService: ClientService,
              private toastService: ToastService,
              private parkerTurnoverReportClient: ParkerTurnoverReportClient) {
    super();
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<PagedResultDTOOfParkerTurnoverReportDTO> {
    if (!this.searchParameters.dateRange.startDate || !this.searchParameters.dateRange.endDate) {
      return of(new PagedResultDTOOfParkerTurnoverReportDTO());
    }
    if (!sortBy) {
      sortBy = 'Id';
    }
    if (!sortDirection) {
      sortDirection = SortDirection.Descending;
    }

    return this.parkerTurnoverReportClient.searchParkerTurnoverReportData(this.clientService.selectedClient.id, this.searchParameters, skip, take, sortBy, sortDirection).pipe(
      tap(pagedResult => {
        this.totalAmount = pagedResult.items.reduce((prev, current) => prev + current.amount, 0);
      }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      }),
    );
  }

  public addRecord(data: any): Observable<any> {
    throw new Error('Method not implemented.');
  }

  public updateRecord(data: any): Observable<any> {
    throw new Error('Method not implemented.');
  }

  public deleteRecord(id: number): Observable<any> {
    throw new Error('Method not implemented.');
  }

  public exportReport(parkerTurnoverReportParameterDTO: ParkerTurnoverReportParameterDTO, saveOptions: SaveOptions): Observable<SavedReportFileDTO> {
    return this.parkerTurnoverReportClient.exportParkerTurnoverReport(
      this.clientService.selectedClient.id,
      saveOptions.exportType,
      saveOptions.saveReport,
      parkerTurnoverReportParameterDTO,
    )
      .pipe(
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }

  public getReportSummary(parkerTurnoverReportParameterDTO: ParkerTurnoverReportParameterDTO): Observable<ParkerTurnoverReportSummaryDTO> {
    return this.parkerTurnoverReportClient.summarizeParkerTurnoverReport(this.clientService.selectedClient.id, parkerTurnoverReportParameterDTO)
      .pipe(
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }

  public searchZones(searchText: string): Observable<PagedResultDTOOfZoneDTO> {
    const filterDTOs = [];
    filterDTOs.push(new FilterDTO({
      isComplex: false,
      field: 'Name',
      operator: FilterOperator.Contains,
      value: searchText,
    }));
    return this.zoneClient.searchZones(this.clientService.selectedClient.id, filterDTOs, 0, 9, 'Name', SortDirection.Ascending)
      .pipe(
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }

  public searchParkerDropdownValues(searchText: string): Observable<PagedResultDTOOfParkerDTO> {
    const filterDTOs = [];
    if (searchText) {
      filterDTOs.push(new FilterDTO({
        isComplex: true,
        condition: FilterCondition.Or,
        predicates: [
          new FilterDTO({
            isComplex: false,
            field: 'FirstName',
            operator: FilterOperator.Contains,
            value: searchText,
          }),
          new FilterDTO({
            isComplex: false,
            field: 'LastName',
            operator: FilterOperator.Contains,
            value: searchText,
          }),
        ],
      }));
    }


    return this.parkerClient.searchParkers(this.clientService.selectedClient.id, filterDTOs, 0, 9, 'Firstname', SortDirection.Ascending, false, false)
      .pipe(
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }
}
